import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BarChart3, MapPin, Tag, Sparkles, Check, X } from 'lucide-react';
import { TeamAApi, type StatsReport } from '@ai-tiaozs/shared';

/**
 * A 队 · ④ 互助与防丢报告
 *
 * 数据由"捡到者发布 + 失主确认取回"两端驱动
 * 找回率 = 失主自助确认"我已取回" / 总发布
 */
export function TeamAStats() {
  const [stats, setStats] = useState<StatsReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    TeamAApi.stats()
      .then(setStats)
      .catch((e) => setError(e?.message ?? '加载失败'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-xs text-slate-500">统计加载中…</p>;
  if (error) return <p className="text-xs text-teamA-danger">{error}</p>;
  if (!stats) return null;

  const maxCat = Math.max(1, ...stats.byCategory.map((c) => c.count));
  const maxLoc = Math.max(1, ...stats.byFoundLocation.map((c) => c.count));

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-lg font-bold">④ 互助与防丢报告</h1>
        <p className="text-xs text-slate-500">捡到者发布 + 失主自助取回</p>
      </header>

      {/* 顶部三联:总数 / 找回 / 找回率 */}
      <section className="card grid grid-cols-3 gap-2 text-center">
        <div>
          <p className="text-2xl font-bold text-teamA-primary">{stats.total}</p>
          <p className="text-[11px] text-slate-500">发布失物数</p>
        </div>
        <div>
          <p className="text-2xl font-bold text-teamA-safe">{stats.returned}</p>
          <p className="text-[11px] text-slate-500">已找回数</p>
        </div>
        <div>
          <p className="text-2xl font-bold text-teamA-warning">
            {Math.round(stats.returnRate * 100)}%
          </p>
          <p className="text-[11px] text-slate-500">找回率</p>
        </div>
      </section>

      <p className="rounded-md bg-teamA-primary/5 px-3 py-2 text-[11px] text-slate-700">
        💡 找回数据由同学在自助领取后主动确认,体现校园互助与诚信使用。
      </p>

      {/* 状态分布 */}
      <section className="card space-y-2">
        <h2 className="text-sm font-semibold text-slate-700">📊 状态分布</h2>
        <ul className="grid grid-cols-3 gap-2 text-xs">
          <li className="rounded-md bg-teamA-warning/5 p-2 text-center">
            <p className="text-lg font-bold text-teamA-warning">{stats.available}</p>
            <p className="text-slate-500">等待寻找</p>
          </li>
          <li className="rounded-md bg-teamA-safe/5 p-2 text-center">
            <p className="text-lg font-bold text-teamA-safe">{stats.returned}</p>
            <p className="text-slate-500">已找回</p>
          </li>
          <li className="rounded-md bg-slate-50 p-2 text-center">
            <p className="text-lg font-bold text-slate-600">{stats.expired}</p>
            <p className="text-slate-500">已过期</p>
          </li>
        </ul>
      </section>

      {/* AI 寻找表现 */}
      <section className="card space-y-2">
        <h2 className="flex items-center gap-1 text-sm font-semibold text-slate-700">
          <Sparkles className="h-4 w-4" /> AI 寻找表现
        </h2>
        <ul className="space-y-1 text-xs text-slate-700">
          <li>AI 自动建档(完整填写特征+存放点):{stats.aiBuildTestCorrect} 件</li>
          <li>文字寻找 Top-3 命中:{stats.textSearchTop3Hit} 次</li>
          <li>语音寻找 Top-3 命中:{stats.voiceSearchTop3Hit} 次</li>
          <li>参考照片寻找 Top-3 命中:{stats.imageSearchTop3Hit} 次</li>
        </ul>
      </section>

      {/* 自助领取反馈 */}
      <section className="card space-y-2">
        <h2 className="flex items-center gap-1 text-sm font-semibold text-slate-700">
          <Check className="h-4 w-4" /> 自助领取反馈
        </h2>
        <ul className="space-y-1 text-xs text-slate-700">
          <li>
            <Check className="mr-1 inline h-3 w-3 text-teamA-safe" />
            学生点击"我已取回":{stats.foundCount} 次
          </li>
          <li>
            <X className="mr-1 inline h-3 w-3 text-teamA-warning" />
            "没有找到"反馈:{stats.notFoundCount} 次
          </li>
          <li>
            <X className="mr-1 inline h-3 w-3 text-teamA-danger" />
            "不是我的"反馈:{stats.notMineCount} 次
          </li>
        </ul>
      </section>

      {/* 类别 */}
      <section className="card space-y-2">
        <h2 className="flex items-center gap-1 text-sm font-semibold text-slate-700">
          <Tag className="h-4 w-4" /> 失物按类别
        </h2>
        {stats.byCategory.length === 0 ? (
          <p className="text-xs text-slate-500">暂无数据</p>
        ) : (
          <ul className="space-y-1">
            {stats.byCategory.map((c) => (
              <li key={c.category} className="flex items-center gap-2 text-xs">
                <span className="w-16 shrink-0 text-slate-600">{c.category}</span>
                <div className="h-2 flex-1 overflow-hidden rounded bg-slate-100">
                  <div
                    className="h-full bg-teamA-primary"
                    style={{ width: `${(c.count / maxCat) * 100}%` }}
                  />
                </div>
                <span className="w-8 text-right text-slate-500">{c.count}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* 捡到地点 */}
      <section className="card space-y-2">
        <h2 className="flex items-center gap-1 text-sm font-semibold text-slate-700">
          <MapPin className="h-4 w-4" /> 失物按捡到地点 Top 10
        </h2>
        {stats.byFoundLocation.length === 0 ? (
          <p className="text-xs text-slate-500">暂无数据</p>
        ) : (
          <ul className="space-y-1">
            {stats.byFoundLocation.map((c) => (
              <li key={c.location} className="flex items-center gap-2 text-xs">
                <span className="w-24 shrink-0 truncate text-slate-600">{c.location}</span>
                <div className="h-2 flex-1 overflow-hidden rounded bg-slate-100">
                  <div
                    className="h-full bg-teamA-warning"
                    style={{ width: `${(c.count / maxLoc) * 100}%` }}
                  />
                </div>
                <span className="w-8 text-right text-slate-500">{c.count}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* 防丢建议 */}
      <section className="card space-y-2">
        <h2 className="flex items-center gap-1 text-sm font-semibold text-slate-700">
          <BarChart3 className="h-4 w-4" /> 防丢建议
        </h2>
        <ul className="list-inside list-disc space-y-1 text-xs text-slate-600">
          {stats.advice.map((a, i) => (
            <li key={i}>{a}</li>
          ))}
        </ul>
      </section>

      <Link to="/" className="btn-outline w-full">返回失物墙</Link>
    </div>
  );
}
